/**
 * The KPI band: how the SASE estate splits across Cato's four connectivity
 * states, plus the fleet flags that matter before anything goes red.
 *
 * `degraded` is the headline the rest of the board hangs off — a site still
 * passing traffic with a dead WAN2, a downed HA secondary, or a sick tunnel.
 */

import { Card, SegmentBar, Statistic, StatisticBar, Typography } from "@norican/react";

import type { NetworkSummary } from "@/lib/api";

import { STATE_COLOR, STATE_LABEL, STATE_ORDER, stateCounts } from "./status";

interface SaseSummaryProps {
  /** Estate-wide rollup from `/network/overview`. */
  summary: NetworkSummary;
}

export function SaseSummary({ summary }: SaseSummaryProps) {
  const counts = stateCounts(summary.counts);
  const total = summary.counts.total ?? 0;
  const attention = counts.degraded + counts.disconnected;
  const linksDown = summary.links_down ?? 0;
  const linksTotal = summary.links_total ?? 0;
  const haSecondaryDown = summary.ha_secondary_down ?? 0;

  // Only show states that actually occur — an empty segment is just noise.
  const segments = STATE_ORDER.filter((state) => counts[state] > 0).map((state) => ({
    value: counts[state],
    label: `${STATE_LABEL[state]} (${counts[state]})`,
    color: STATE_COLOR[state],
  }));

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card variant="outline" accent="left" accentTone={attention === 0 ? "success" : "danger"}>
          <Card.Body>
            <Statistic
              label="Sites connected"
              value={`${counts.connected} / ${total}`}
              tone={attention === 0 ? "success" : "danger"}
              size="lg"
              caption={
                counts.disabled > 0
                  ? `${counts.disabled} disabled (not a fault)`
                  : "Every site reporting to Cato"
              }
            />
          </Card.Body>
        </Card>

        <Card
          variant="outline"
          accent="left"
          accentTone={counts.degraded > 0 ? "warning" : "accent"}
        >
          <Card.Body>
            <Statistic
              label="Degraded"
              value={String(counts.degraded)}
              tone={counts.degraded > 0 ? "warning" : "neutral"}
              size="lg"
              caption="Passing traffic with a link, tunnel or HA fault"
            />
          </Card.Body>
        </Card>

        <Card
          variant="outline"
          accent="left"
          accentTone={counts.disconnected > 0 ? "danger" : "accent"}
        >
          <Card.Body>
            <Statistic
              label="Disconnected"
              value={String(counts.disconnected)}
              tone={counts.disconnected > 0 ? "danger" : "neutral"}
              size="lg"
              caption="No connectivity to the Cato fabric"
            />
          </Card.Body>
        </Card>

        <Card variant="outline" accent="left" accentTone={linksDown > 0 ? "warning" : "accent"}>
          <Card.Body>
            <Statistic
              label="WAN ports up"
              value={`${linksTotal - linksDown} / ${linksTotal}`}
              tone={linksDown > 0 ? "warning" : "neutral"}
              size="lg"
              progress={linksTotal > 0 ? ((linksTotal - linksDown) / linksTotal) * 100 : 0}
              progressTone={linksDown > 0 ? "warning" : "success"}
              caption={
                haSecondaryDown > 0
                  ? `${haSecondaryDown} HA secondary down — no failover`
                  : "Last-mile links terminating in a PoP"
              }
            />
          </Card.Body>
        </Card>
      </div>

      <Card variant="subtle">
        <Card.Body>
          <StatisticBar
            aria-label="SASE fleet summary"
            size="sm"
            items={[
              { label: "Sites", value: String(total) },
              { label: "Sockets", value: String(summary.devices_total ?? 0) },
              {
                label: "Sockets down",
                value: String(summary.devices_down ?? 0),
                tone: (summary.devices_down ?? 0) > 0 ? "negative" : "neutral",
              },
              {
                label: "Firmware versions",
                value: String(summary.firmware_versions?.length ?? 0),
                tone: summary.firmware_skew ? "caution" : "neutral",
                caption: summary.firmware_skew ? "fleet is not uniform" : "fleet is uniform",
              },
              {
                label: "WAN ports down",
                value: String(linksDown),
                tone: linksDown > 0 ? "negative" : "neutral",
                align: "end",
              },
            ]}
          />
        </Card.Body>
      </Card>

      {segments.length > 0 && (
        <Card variant="subtle">
          <Card.Body>
            <Typography
              variant="caption"
              as="p"
              className="mb-3 font-semibold tracking-wide uppercase"
            >
              Estate composition
            </Typography>
            <SegmentBar
              segments={segments}
              size="lg"
              showLegend
              aria-label="Cato sites by connectivity status"
            />
          </Card.Body>
        </Card>
      )}
    </div>
  );
}
